import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ActionCategoryList } from '../../common/actions';
import { NewActionDetail } from './NewActionDetail';
import ActionFormModal from './ActionFormModal';
import { drapcodeIcon } from '../../common/custom/icon-library';
import { toast } from 'react-toastify';
import { convertStringToLabel, thirdPartyAuth } from '../../common/utils/page_utils';
import ActionEnableModal from './ActionEnableModal';
import {
  AIRTABLE,
  APPWRITE,
  APP_WRITE,
  DIRECTUS,
  SUPABASE,
  XANO,
  XATA,
  pluginCode,
  OAUTH2,
  MYSQL,
} from 'drapcode-constant';

const dropdownStyle = {
  marginBottom: '-20px',
  zIndex: 10,
  position: 'inherit',
  margin: '-5px 0 -15px',
};

const disallowedActionNames = [
  'goToPage',
  'dynamicPageRedirect',
  'customPageRedirect',
  'reloadPage',
];

class NewEventAction extends Component {
  static propTypes = {
    events: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
  };

  state = {
    selectedIndex: null,
  };

  changeSelectedIndex = (index) => {
    this.setState({ selectedIndex: index });
  };

  synthesizeLabel = (str) => {
    const specialTerms = ['API', 'PDF', '(', 'AWS', 'SNS'];
    const hasSpecialTerm = specialTerms.some((term) => str.includes(term));
    return hasSpecialTerm ? str : convertStringToLabel(str, ' ');
  };

  handleClick = (action) => {
    const selectValue = action;
    const { events, eventActions, actions, projectId, version } = this.props;
    const { selectedEvent } = events;
    const currentActionObj = eventActions.find((obj) => obj.name === action.name);
    const cannotDuplicate = [
      'socialLoginSignup',
      'loginUser',
      'loginUserWithToken',
      'signupAndLoginUser',
      'signupAndLoginAnonymousUser',
    ];
    if (currentActionObj && cannotDuplicate.includes(currentActionObj.name)) {
      toast.error(`Can't add another ${currentActionObj.label} action.`);
    } else {
      let step = 1;
      if (selectedEvent) {
        const { actions } = selectedEvent;
        if (actions && actions.length) {
          step = actions.length + 1;
        }
      }
      const actionData = {
        name: selectValue.name,
        label: selectValue.label,
        step: step,
        parameters: [],
      };

      if (selectValue.source) {
        actionData['source'] = selectValue.source;
      }

      actions.saveNewAction({
        projectId: projectId,
        eventId: selectedEvent.uuid,
        data: actionData,
        version,
      });
      actions.openActionPropModal(actionData);
    }
  };

  hideFormModal = () => {
    this.props.actions.closeActionFormModal();
  };

  hideEnableFormModal = () => {
    this.props.actions.closeActionEnableModal();
  };

  filterActionItems = (actionItems) => {
    const { plugins } = this.props.events;
    if (!plugins || plugins.length === 0) {
      actionItems = _.filter(actionItems, function (item) {
        return !thirdPartyAuth.includes(item.name);
      });
      return actionItems;
    }

    const firebaseLogin = plugins.find((plugin) => plugin.code === pluginCode.LOGIN_WITH_FIREBASE);
    if (!firebaseLogin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['signUpUserWithFirebase', 'loginUserWithFirebase'].includes(item.name);
      });
    }
    const xanoLogin = plugins.find((plugin) => plugin.code === pluginCode.LOGIN_WITH_XANO);
    if (!xanoLogin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['signUpUserWithXano', 'loginUserWithXano'].includes(item.name);
      });
    }
    const oAuth2Login = plugins.find((plugin) => plugin.code === pluginCode.OAUTH_2);
    if (!oAuth2Login) {
      actionItems = _.filter(actionItems, function (item) {
        return !['loginWithOAuth2', 'signUpWithOAuth2'].includes(item.name);
      });
    }
    const twoFactorLogin = plugins.find(
      (plugin) => plugin.code === pluginCode.TWO_FACTOR_AUTHENTICATION,
    );
    if (!twoFactorLogin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['verifyTwoFactorAuthentication'].includes(item.name);
      });
    }
    const facebookLogin = plugins.find((plugin) => plugin.code === pluginCode.FACEBOOK_LOGIN);
    if (!facebookLogin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['loginWithFacebook', 'signUpWithFacebook'].includes(item.name);
      });
    }
    const twitterLogin = plugins.find((plugin) => plugin.code === pluginCode.TWITTER_LOGIN);
    if (!twitterLogin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['loginWithTwitter', 'signUpWithTwitter'].includes(item.name);
      });
    }
    const emailOtpAuthenticator = plugins.find(
      (plugin) => plugin.code === pluginCode.EMAIL_OTP_AUTHENTICATOR,
    );
    if (!emailOtpAuthenticator) {
      actionItems = _.filter(actionItems, function (item) {
        return !['generateEmailOTP', 'verifyEmailOTP', 'generateOnlyEmailOTP'].includes(item.name);
      });
    }
    const smsOtpAuthenticator = plugins.find(
      (plugin) => plugin.code === pluginCode.SMS_OTP_AUTHENTICATOR,
    );
    if (!smsOtpAuthenticator) {
      actionItems = _.filter(actionItems, function (item) {
        return !['generateSmsOTP', 'verifySmsOTP', 'generateOnlySMSOTP'].includes(item.name);
      });
    }
    const emailOtpConsentValidator = plugins.find(
      (plugin) => plugin.code === pluginCode.EMAIL_OTP_CONSENT_VALIDATOR,
    );
    if (!emailOtpConsentValidator) {
      actionItems = _.filter(actionItems, function (item) {
        return ![
          'generateUserConsentEmailOTP',
          'verifyUserConsentEmailOTP',
          'generateOnlyUserConsentEmailOTP',
        ].includes(item.name);
      });
    }
    const smsOtpConsentValidator = plugins.find(
      (plugin) => plugin.code === pluginCode.SMS_OTP_CONSENT_VALIDATOR,
    );
    if (!smsOtpConsentValidator) {
      actionItems = _.filter(actionItems, function (item) {
        return ![
          'generateUserConsentSmsOTP',
          'verifyUserConsentSmsOTP',
          'generateOnlyUserConsentSmsOTP',
        ].includes(item.name);
      });
    }
    const socketIoPlugin = plugins.find((plugin) => plugin.code === pluginCode.SOCKET_IO);
    if (!socketIoPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['sendSocketMessage'].includes(item.name);
      });
    }
    const signzyAccountAggregatorPlugin = plugins.find(
      (plugin) => plugin.code === pluginCode.SIGNZY_ACCOUNT_AGGREGATOR,
    );
    if (!signzyAccountAggregatorPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['createConsentRequest', 'fetchFinancialInformation'].includes(item.name);
      });
    }
    const signzyDigitalDocumentSigningPlugin = plugins.find(
      (plugin) => plugin.code === pluginCode.SIGNZY_DIGITAL_DOCUMENT_SIGNING,
    );
    if (!signzyDigitalDocumentSigningPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['signzyDigitalDocSign', 'fetchContractDetails'].includes(item.name);
      });
    }
    const googleCalendarPlugin = plugins.find(
      (plugin) => plugin.code === pluginCode.GOOGLE_CALENDAR,
    );
    if (!googleCalendarPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return ![
          'initiateGoogleCalendarAuth',
          'fetchGoogleCalendarEvents',
          'syncGoogleCalendarEvents',
          'scheduleGoogleCalendarEvent',
          'deleteGoogleCalendarEvent',
        ].includes(item.name);
      });
    }
    const googleMeetPlugin = plugins.find((plugin) => plugin.code === pluginCode.GOOGLE_MEET);
    if (!googleMeetPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['initiateGoogleMeetAuth', 'createInstantGoogleMeet'].includes(item.name);
      });
    }
    const iprsKenyaPlugin = plugins.find((plugin) => plugin.code === pluginCode.IPRS_KENYA);
    if (!iprsKenyaPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['iprsKenya'].includes(item.name);
      });
    }
    const metropolCreditReportPlugin = plugins.find(
      (plugin) => plugin.code === pluginCode.METROPOL_CREDIT_REPORTS_BUREAU_KENYA,
    );
    if (!metropolCreditReportPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['metropolCreditReport'].includes(item.name);
      });
    }
    const kraPinCheckerPlugin = plugins.find(
      (plugin) => plugin.code === pluginCode.KRA_PIN_CHECKER,
    );
    if (!kraPinCheckerPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['kraPinChecker'].includes(item.name);
      });
    }
    const eStatementAnalysisPlugin = plugins.find(
      (plugin) => plugin.code === pluginCode.E_STATEMENT_ANALYSIS,
    );
    if (!eStatementAnalysisPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return !['eStatementAnalysis'].includes(item.name);
      });
    }
    const boldSignPlugin = plugins.find((plugin) => plugin.code === pluginCode.BOLDSIGN);
    if (!boldSignPlugin) {
      actionItems = _.filter(actionItems, function (item) {
        return ![
          'boldSignSendDocument',
          'boldSignDocumentStatus',
          'boldSignEmbeddedRequest',
        ].includes(item.name);
      });
    }
    return actionItems;
  };

  renderImage = (image, label) => {
    return (
      <img src={require(`../../common/dataSource/img/${image}`)} alt={label} className="mr-3 svg" />
    );
  };

  showIcon = (action, category) => {
    switch (action.name) {
      case 'triggerZap':
        return this.renderImage('zapier.svg', action.label);
      case 'loginWithOAuth2':
      case 'signUpWithOAuth2':
        return this.renderImage('oAuth2-logo.svg', action.label);
      case 'updateCellData':
        return this.renderImage('Tabulator.png', action.label);
      case 'createConsentRequest':
      case 'fetchFinancialInformation':
      case 'signzyDigitalDocSign':
      case 'fetchContractDetails':
        return this.renderImage('signzy.svg', action.label);
      case 'fetchGoogleCalendarEvents':
      case 'initiateGoogleCalendarAuth':
      case 'syncGoogleCalendarEvents':
      case 'scheduleGoogleCalendarEvent':
      case 'deleteGoogleCalendarEvent':
      case 'updateGoogleCalendarEvent':
        return this.renderImage('google-calendar.svg', action.label);
      case 'initiateGoogleMeetAuth':
      case 'createInstantGoogleMeet':
        return this.renderImage('google-meet.svg', action.label);
      case 'initiateZoomAuth':
      case 'createInstantZoomMeet':
        return this.renderImage('zoom.svg', action.label);
      case 'sendDataToExternalApi':
      case 'sendDataToExternalApiAndAuthorize':
      case 'sendDataToExternalApiAndFileDownload':
      case 'sendDataToExternalSource':
      case 'sendDataToExternalSourceAndFileDownload':
      case 'authorizeWithExternalDataSource':
        if (category === 'Supabase') {
          return this.renderImage('supabase.svg', action.label);
        } else if (category === 'Directus') {
          return this.renderImage('directus.svg', action.label);
        } else if (category === 'Appwrite') {
          return this.renderImage('appwrite.svg', action.label);
        } else if (category === 'Xano') {
          return this.renderImage('xano.svg', action.label);
        } else if (category === 'MySQL') {
          return this.renderImage('mysql.svg', action.label);
        } else {
          return <i className={`mr-3 wb ${drapcodeIcon(action.name)}`} />;
        }
      case 'addDataKnowledgeBase':
        return <i className={`wb ${drapcodeIcon('saveItemInMetaDataTable')}`} />;
      case 'queryFromKnowledgeBase':
      case 'answerFromChatbot':
      case 'continueConversation':
        return <i className={`wb ${drapcodeIcon('AI / ChatGPT')}`} />;
      case 'initiateBoxAuth':
      case 'uploadFileToBox':
        return this.renderImage('box.svg', action.label);
      case 'generateQR':
      case 'initiateQRCodeReader':
        return this.renderImage('qr.svg', action.label);
      case 'initiateMPesaC2BPayment':
      case 'initiateMPesaB2CPayout':
      case 'registerMpesaC2BUrls':
        return this.renderImage('m-pesa.svg', action.label);
      case 'metropolCreditReport':
      case 'iprsKenya':
      case 'kraPinChecker':
      case 'eStatementAnalysis':
        return this.renderImage('spin-mobile.svg', action.label);
      case 'boldSignSendDocument':
      case 'boldSignDocumentStatus':
      case 'boldSignEmbeddedRequest':
        return this.renderImage('boldsign.svg', action.label);
      default:
        return <i className={`mr-3 wb ${drapcodeIcon(action.name)}`} />;
    }
  };

  showCategoryIcon = (category) => {
    switch (category.toUpperCase()) {
      case OAUTH2:
        return this.renderImage('oAuth2-logo.svg', 'Oauth2');
      case SUPABASE:
        return this.renderImage('supabase.svg', 'Supabase');
      case DIRECTUS:
        return this.renderImage('directus.svg', 'Directus');
      case APPWRITE:
        return this.renderImage('appwrite.svg', 'Appwrite');
      case XANO:
        return this.renderImage('xano.svg', 'Xano');
      case 'TABULATOR':
        return this.renderImage('Tabulator.png', 'Tabulator');
      case 'SIGNZY':
        return this.renderImage('signzy.svg', 'Signzy');
      case 'PINECONE':
        return this.renderImage('pinecone.png', 'Pinecone');
      case 'AI CHATBOT':
        return this.renderImage('ai-chatbot.png', 'AI Chatbot');
      case 'ZOOM':
        return this.renderImage('zoom.svg', 'Zoom');
      case MYSQL:
        return this.renderImage('mysql.svg', 'MySQL');
      case 'BOX':
        return this.renderImage('box.svg', 'Box');
      case 'QR':
        return this.renderImage('qr.svg', 'QR');
      case 'M-PESA':
        return this.renderImage('m-pesa.svg', 'M-Pesa');
      case 'SPIN MOBILE':
        return this.renderImage('spin-mobile.svg', 'Spin Mobile');
      case 'BOLDSIGN':
        return this.renderImage('boldsign.svg', 'BoldSign');
      default:
        return <i className={`mr-3 wb ${drapcodeIcon(category)}`} />;
    }
  };

  showSupabaseActionItems = (action, isNested = false) => {
    let actionItems = action.items;
    if (action.category.includes('Login')) {
      actionItems = this.filterActionItems(actionItems);
    }
    const actionCategory = action.category;
    const actionCategoryUppercase = actionCategory.toUpperCase();

    const content = (
      <ul className="dropdown-menu list-group action-menu">
        {actionCategoryUppercase === SUPABASE && (
          <li className="list-group-item disabled fw-bold">Auth</li>
        )}

        {actionItems
          .filter((item) => item.type === 'AUTH')
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" className="svg-container" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}

        {actionCategoryUppercase === SUPABASE && (
          <li className="list-group-item disabled fw-bold">Others</li>
        )}

        {actionItems
          .filter((item) => !item.type)
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" className="svg-container" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
      </ul>
    );

    //  KEY FIX — wrap ONLY when not nested
    if (isNested) return content;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory)}
          {actionCategory}
        </a>
        {content}
      </li>
    );
  };

  showDirectusActionItems = (action, isNested = false) => {
    let actionItems = action.items;

    if (action.category.includes('Login')) {
      actionItems = this.filterActionItems(actionItems);
    }

    const actionCategory = action.category;
    const actionCategoryUppercase = actionCategory.toUpperCase();

    //  Extract ONLY the dropdown content
    const content = (
      <ul className="dropdown-menu list-group action-menu">
        {actionCategoryUppercase === DIRECTUS && (
          <li
            className="list-group-item disabled text-muted"
            aria-disabled="true"
            style={{ fontWeight: 'bold' }}
          >
            <span>Auth</span>
          </li>
        )}

        {actionItems
          .filter((item) => item.type === 'AUTH')
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}

        {actionCategoryUppercase === DIRECTUS && (
          <li
            className="list-group-item disabled text-muted"
            aria-disabled="true"
            style={{ fontWeight: 'bold' }}
          >
            <span>Others</span>
          </li>
        )}

        {actionItems
          .filter((item) => !item.type)
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
      </ul>
    );

    //  KEY FIX — when used inside datasource, return ONLY content
    if (isNested) return content;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory)}
          {actionCategory}
        </a>
        {content}
      </li>
    );
  };

  showXanoActionItems = (action, isNested = false) => {
    let actionItems = action.items;

    if (action.category.includes('Login')) {
      actionItems = this.filterActionItems(actionItems);
    }

    const actionCategory = action.category;
    const actionCategoryUppercase = actionCategory.toUpperCase();

    const content = (
      <ul className="dropdown-menu list-group action-menu">
        {actionCategoryUppercase === XANO && (
          <li className="list-group-item disabled fw-bold">Auth</li>
        )}

        {actionItems
          .filter((item) => item.type === 'AUTH')
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}

        {actionCategoryUppercase === XANO && (
          <li className="list-group-item disabled fw-bold">Others</li>
        )}

        {actionItems
          .filter((item) => !item.type)
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
      </ul>
    );

    //  KEY FIX
    if (isNested) return content;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory)}
          {actionCategory}
        </a>
        {content}
      </li>
    );
  };

  showAppwriteActionItems = (action, isNested = false) => {
    let actionItems = action.items;

    if (action.category.includes('Login')) {
      actionItems = this.filterActionItems(actionItems);
    }

    const actionCategory = action.category;
    const actionCategoryUppercase = actionCategory.toUpperCase();

    //  third-level content only
    const content = (
      <ul className="dropdown-menu list-group action-menu">
        {actionCategoryUppercase === APPWRITE && (
          <li
            className="list-group-item disabled text-muted"
            aria-disabled="true"
            style={{ fontWeight: 'bold' }}
          >
            <span>Auth</span>
          </li>
        )}

        {actionItems
          .filter((item) => item.type === 'AUTH')
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" className="svg-container" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}

        {actionCategoryUppercase === APPWRITE && (
          <li
            className="list-group-item disabled text-muted"
            aria-disabled="true"
            style={{ fontWeight: 'bold' }}
          >
            <span>Others</span>
          </li>
        )}

        {actionItems
          .filter((item) => !item.type)
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" className="svg-container" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
      </ul>
    );

    //  KEY FIX — nested usage
    if (isNested) return content;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory)}
          {actionCategory}
        </a>
        {content}
      </li>
    );
  };

  showOAuth2ActionItems = (action, isNested = false) => {
    let actionItems = action.items;

    if (action.category.includes(OAUTH2)) {
      actionItems = this.filterActionItems(actionItems);
    }

    const actionCategory = action.category;

    //  third-level content only
    const content = (
      <ul className="dropdown-menu list-group action-menu">
        {actionItems
          .filter((item) => !item.type)
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" className="svg-container" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
      </ul>
    );

    //  KEY FIX — nested usage
    if (isNested) return content;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory)}
          {actionCategory}
        </a>
        {content}
      </li>
    );
  };

  showMySqlActionItems = (action, isNested = false) => {
    let actionItems = action.items;

    if (action.category.includes('Login')) {
      actionItems = this.filterActionItems(actionItems);
    }

    const actionCategory = action.category;
    const actionCategoryUppercase = actionCategory.toUpperCase();

    //  third-level content only
    const content = (
      <ul className="dropdown-menu list-group action-menu">
        {actionCategoryUppercase === MYSQL && (
          <li
            className="list-group-item disabled text-muted"
            aria-disabled="true"
            style={{ fontWeight: 'bold' }}
          >
            <span>Auth</span>
          </li>
        )}

        {actionItems
          .filter((item) => item.type === 'AUTH')
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}

        {actionCategoryUppercase === MYSQL && (
          <li
            className="list-group-item disabled text-muted"
            aria-disabled="true"
            style={{ fontWeight: 'bold' }}
          >
            <span>Others</span>
          </li>
        )}

        {actionItems
          .filter((item) => !item.type)
          .map((item, index) => (
            <li key={item.name + index}>
              <a tabIndex="-1" onClick={() => this.handleClick(item)}>
                {this.showIcon(item, actionCategory)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
      </ul>
    );

    //  KEY FIX — nested usage
    if (isNested) return content;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory)}
          {actionCategory}
        </a>
        {content}
      </li>
    );
  };

  renderThirdLevel = (sub) => {
    switch (sub.category.toUpperCase()) {
      case SUPABASE:
        return this.showSupabaseActionItems(sub, true);
      case DIRECTUS:
        return this.showDirectusActionItems(sub, true);
      case XANO:
        return this.showXanoActionItems(sub, true);
      case APPWRITE:
        return this.showAppwriteActionItems(sub, true);
      case OAUTH2:
        return this.showOAuth2ActionItems(sub, true);
      // case MYSQL:
      //   return this.showMySqlActionItems(sub, true);

      default:
        return this.showDefaultActionItems(sub);
    }
  };

  showDefaultActionItems = (sub) => (
    <ul className="dropdown-menu third-level">
      {sub.items &&
        sub.items.map((item, index) => (
          <li key={item.name + index}>
            <a tabIndex="-1" className="svg-container" onClick={() => this.handleClick(item)}>
              {this.showIcon(item, sub.name)}&nbsp;
              {this.synthesizeLabel(item.label)}
            </a>
          </li>
        ))}
    </ul>
  );

  renderThreeLevelDropdown = (actionCategory) => {
    if (
      !actionCategory ||
      !actionCategory.subCategories ||
      actionCategory.subCategories.length === 0
    )
      return null;

    return (
      <li className="dropdown-submenu first-level" key={actionCategory.category}>
        <a tabIndex="-1">
          {this.showCategoryIcon(actionCategory.category)}
          {actionCategory.category}
        </a>
        <ul className="dropdown-menu multi-level">
          {actionCategory.subCategories.map((sub, sIndex) => (
            <li className="dropdown-submenu second-level" key={sIndex}>
              <a tabIndex="-1">
                {this.showCategoryIcon(sub.category)}
                {sub.name}
              </a>

              {this.renderThirdLevel(sub)}
            </li>
          ))}
        </ul>
      </li>
    );
  };

  showActionItems = (action) => {
    let actionItems = action.items;
    if (action.subCategories) {
      return this.renderThreeLevelDropdown(action);
    }
    if (
      [
        'Login / Signup',
        'OTP Authentication',
        'Notification',
        'Signzy',
        'Google',
        'Spin Mobile',
      ].includes(action.category)
    ) {
      actionItems = this.filterActionItems(actionItems);
    }

    return (
      <li className="dropdown-submenu first-level" key={action.category}>
        <a tabIndex="-1">
          {this.showCategoryIcon(action.category)}
          {action.category}
        </a>
        <ul className="dropdown-menu list-group action-menu">
          {actionItems.map((item, index) => (
            <li key={item.name + index} style={{ width: 'max-content', minWidth: '100%' }}>
              <a
                tabIndex="-1"
                className="svg-container"
                key={item.name + index}
                onClick={() => this.handleClick(item)}
              >
                {this.showIcon(item, action.category)}&nbsp;
                {this.synthesizeLabel(item.label)}
              </a>
            </li>
          ))}
        </ul>
      </li>
    );
    // switch (action.category.toUpperCase()) {
    //   case SUPABASE:
    //     return this.showSupabaseActionItems(action);
    //   case DIRECTUS:
    //     return this.showDirectusActionItems(action);
    //   case XANO:
    //     return this.showXanoActionItems(action);
    //   case APPWRITE:
    //     return this.showAppwriteActionItems(action);
    //   case OAUTH2:
    //     return this.showOAuth2ActionItems(action);
    //   // case MYSQL:
    //   //   return this.showMySqlActionItems(action);
    //   default:
    //     return (
    //       <li className="dropdown-submenu first-level" key={action.category}>
    //         <a tabIndex="-1">
    //           {this.showCategoryIcon(action.category)}
    //           {action.category}
    //         </a>
    //         <ul className="dropdown-menu list-group action-menu">
    //           {actionItems.map((item, index) => (
    //             <li key={item.name + index} style={{ width: 'max-content', minWidth: '100%' }}>
    //               <a
    //                 tabIndex="-1"
    //                 className="svg-container"
    //                 key={item.name + index}
    //                 onClick={() => this.handleClick(item)}
    //               >
    //                 {this.showIcon(item, action.category)}&nbsp;
    //                 {this.synthesizeLabel(item.label)}
    //               </a>
    //             </li>
    //           ))}
    //         </ul>
    //       </li>
    //     );
    // }
  };

  filterPluginEventActions = (actionCategoryList, plugins, pCode) => {
    const pluginExist = plugins && plugins.find((e) => e.code === pCode);
    let filterPluginCategory = !!!pluginExist;
    let eventCategoryName = '';
    switch (pCode) {
      case pluginCode.MULTI_TENANT_SAAS:
        eventCategoryName = 'Multi Tenant';
        break;
      case pluginCode.PERMISSIONS_MANAGE:
        eventCategoryName = 'Permission';
        break;
      case pluginCode.CHAT_GPT:
        eventCategoryName = 'AI / ChatGPT';
        break;
      case pluginCode.TWO_FACTOR_AUTHENTICATION:
        eventCategoryName = '2 Factor Authentication';
        break;
      case pluginCode.IMAGINE_PAY:
        eventCategoryName = 'Imagine Pay';
        break;
      case pluginCode.OAUTH_2:
        eventCategoryName = 'OAuth2.0';
        break;
      case pluginCode.LOAN_MANAGEMENT:
        eventCategoryName = 'Loan Management';
        break;
      case pluginCode.AMAZON_TEXTRACT:
        eventCategoryName = 'OCR to Text';
        break;
      case pluginCode.LOGIN:
        eventCategoryName = 'Login / Signup';
        break;
      case pluginCode.EMAIL_OTP_AUTHENTICATOR:
      case pluginCode.SMS_OTP_AUTHENTICATOR:
      case pluginCode.EMAIL_OTP_CONSENT_VALIDATOR:
      case pluginCode.SMS_OTP_CONSENT_VALIDATOR:
        eventCategoryName = 'OTP Authentication';
        const emailOtpExist =
          plugins && plugins.some((e) => e.code === pluginCode.EMAIL_OTP_AUTHENTICATOR);
        const smsOtpExist =
          plugins && plugins.some((e) => e.code === pluginCode.SMS_OTP_AUTHENTICATOR);
        const emailConsentOtpExist =
          plugins && plugins.some((e) => e.code === pluginCode.EMAIL_OTP_CONSENT_VALIDATOR);
        const smsConsentOtpExist =
          plugins && plugins.some((e) => e.code === pluginCode.SMS_OTP_CONSENT_VALIDATOR);
        if (!emailOtpExist && !smsOtpExist && !emailConsentOtpExist && !smsConsentOtpExist) {
          filterPluginCategory = true;
        } else {
          filterPluginCategory = false;
        }
        break;
      case pluginCode.DOCUSIGN:
        eventCategoryName = 'Docusign';
        break;
      case pluginCode.TEXT_ANONYMIZATION_ACTIVITY_TRACKER:
        eventCategoryName = 'Text Anonymization';
        break;
      case pluginCode.TYPESENSE_SEARCH:
        eventCategoryName = 'Typesense Search';
        break;
      case pluginCode.SIGNZY_ACCOUNT_AGGREGATOR:
      case pluginCode.SIGNZY_DIGITAL_DOCUMENT_SIGNING:
        eventCategoryName = 'Signzy';
        const accountAggregatorExists =
          plugins && plugins.some((e) => e.code === pluginCode.SIGNZY_ACCOUNT_AGGREGATOR);
        const digitalDocumentSigningExists =
          plugins && plugins.some((e) => e.code === pluginCode.SIGNZY_DIGITAL_DOCUMENT_SIGNING);
        if (!accountAggregatorExists && !digitalDocumentSigningExists) filterPluginCategory = true;
        else filterPluginCategory = false;
        break;
      case pluginCode.GOOGLE_CALENDAR:
      case pluginCode.GOOGLE_MEET:
        eventCategoryName = 'Google';
        const googleCalendarExists =
          plugins && plugins.some((e) => e.code === pluginCode.GOOGLE_CALENDAR);
        const googleMeetExists = plugins && plugins.some((e) => e.code === pluginCode.GOOGLE_MEET);
        if (!googleCalendarExists && !googleMeetExists) filterPluginCategory = true;
        else filterPluginCategory = false;
        break;
      case pluginCode.PINECONE:
        eventCategoryName = 'Pinecone';
        break;
      case pluginCode.AI_CHATBOT:
        eventCategoryName = 'AI Chatbot';
        break;
      case pluginCode.ZOOM:
        eventCategoryName = 'Zoom';
        break;
      case pluginCode.CIBIL:
        eventCategoryName = 'CIBIL';
        break;
      case pluginCode.BOX:
        eventCategoryName = 'Box';
        break;
      case pluginCode.QR_GENERATOR:
        eventCategoryName = 'QR';
        break;
      case pluginCode.M_PESA:
        eventCategoryName = 'M-Pesa';
        break;
      case pluginCode.SPIN_MOBILE:
        eventCategoryName = 'Spin Mobile';
        break;
      case pluginCode.SIGNATURE_PAD:
        eventCategoryName = 'Signature Pad';
        break;
      case pluginCode.BOLDSIGN:
        eventCategoryName = 'BoldSign';
        break;
      default:
        break;
    }

    if (filterPluginCategory && eventCategoryName) {
      actionCategoryList = actionCategoryList.filter(
        (actionCategory) => actionCategory.category !== eventCategoryName,
      );
    }

    return actionCategoryList;
  };

  filterDataSourceEventActions = (actionCategoryList, dataSources, dataSourceType) => {
    const dataSourceExist = dataSources && dataSources.find((e) => e.type === dataSourceType);
    const filterDataSourceCategory = !!!dataSourceExist;
    let eventCategoryName = '';
    switch (dataSourceType) {
      case SUPABASE:
        eventCategoryName = 'Supabase';
        break;
      case DIRECTUS:
        eventCategoryName = 'Directus';
        break;
      case APP_WRITE:
        eventCategoryName = 'Appwrite';
        break;
      case XANO:
        eventCategoryName = 'Xano';
        break;
      case MYSQL:
        eventCategoryName = 'MySQL';
        break;
      default:
        break;
    }

    if (filterDataSourceCategory && eventCategoryName) {
      actionCategoryList = actionCategoryList.map((actionCategory) => {
        if (actionCategory.type === 'datasource' && Array.isArray(actionCategory.subCategories)) {
          return {
            ...actionCategory,
            subCategories: actionCategory.subCategories.filter(
              (sub) => sub.category !== eventCategoryName,
            ),
          };
        }

        return actionCategory;
      });
    }

    return actionCategoryList;
  };

  filterEventActions = (filterBy, actionCategoryList, sourceList, sourceCode) => {
    switch (filterBy) {
      case 'PLUGIN':
        actionCategoryList = this.filterPluginEventActions(
          actionCategoryList,
          sourceList,
          sourceCode,
        );
        break;
      case 'DATA_SOURCE':
        actionCategoryList = this.filterDataSourceEventActions(
          actionCategoryList,
          sourceList,
          sourceCode,
        );
        break;
      default:
        break;
    }
    return actionCategoryList;
  };

  showActionCategories = () => {
    const { events } = this.props;
    const { plugins, dataSources } = events;
    let actionCategoryList = ActionCategoryList;

    /**
     * Plugin Code list to filter out dependent Event Actions
     * */
    const eventPluginDependency = [
      pluginCode.MULTI_TENANT_SAAS,
      pluginCode.CHAT_GPT,
      pluginCode.PERMISSIONS_MANAGE,
      pluginCode.TWO_FACTOR_AUTHENTICATION,
      pluginCode.IMAGINE_PAY,
      pluginCode.OAUTH_2,
      pluginCode.LOAN_MANAGEMENT,
      pluginCode.AMAZON_TEXTRACT,
      pluginCode.LOGIN,
      pluginCode.DOCUSIGN,
      pluginCode.EMAIL_OTP_AUTHENTICATOR,
      pluginCode.SMS_OTP_AUTHENTICATOR,
      pluginCode.EMAIL_OTP_CONSENT_VALIDATOR,
      pluginCode.SMS_OTP_CONSENT_VALIDATOR,
      pluginCode.TEXT_ANONYMIZATION_ACTIVITY_TRACKER,
      pluginCode.TYPESENSE_SEARCH,
      pluginCode.SIGNZY_ACCOUNT_AGGREGATOR,
      pluginCode.SIGNZY_DIGITAL_DOCUMENT_SIGNING,
      pluginCode.GOOGLE_CALENDAR,
      pluginCode.GOOGLE_MEET,
      pluginCode.PINECONE,
      pluginCode.AI_CHATBOT,
      pluginCode.ZOOM,
      pluginCode.CIBIL,
      pluginCode.BOX,
      pluginCode.QR_GENERATOR,
      pluginCode.M_PESA,
      pluginCode.SPIN_MOBILE,
      pluginCode.IPRS_KENYA,
      pluginCode.METROPOL_CREDIT_REPORTS_BUREAU_KENYA,
      pluginCode.KRA_PIN_CHECKER,
      pluginCode.SIGNATURE_PAD,
      pluginCode.BOLDSIGN,
    ];
    eventPluginDependency.forEach((pluginCode) => {
      actionCategoryList = this.filterEventActions(
        'PLUGIN',
        actionCategoryList,
        plugins,
        pluginCode,
      );
    });

    /**
     * DataSource Type list to filter out dependent Event Actions
     * */
    const eventDataSourceDependency = [SUPABASE, DIRECTUS, APP_WRITE, XANO, XATA, AIRTABLE, MYSQL];
    eventDataSourceDependency.forEach((dataSourceType) => {
      actionCategoryList = this.filterEventActions(
        'DATA_SOURCE',
        actionCategoryList,
        dataSources,
        dataSourceType,
      );
    });

    return (
      <ul className="dropdown-menu multi-level" role="menu" aria-labelledby="dropdownMenu">
        {actionCategoryList.map((action) => this.showActionItems(action))}
      </ul>
    );
  };

  swapActionSteps = (fromActionStep, toActionStep) => {
    const { events, eventActions, projectId, version, actions } = this.props;
    const currentActionObject = eventActions.find((obj) => obj.step === toActionStep.step);

    if (disallowedActionNames.includes(currentActionObject.name)) {
      toast.error(`Can't swap with ${currentActionObject.label} action.`);
    } else {
      const { selectedEvent } = events;
      const eventId = selectedEvent ? selectedEvent.uuid : '';

      if (projectId && eventId) {
        actions.swapActionStep({
          projectId: projectId,
          version,
          eventId: eventId,
          fromActionStep: fromActionStep,
          toActionStep: toActionStep,
        });
      }
    }
  };

  /** The dragstart event is fired when the user starts dragging an element or text selection
   * event.target is the source element : that is dragged
   * Firefox requires calling dataTransfer.setData for the drag to properly work
   * */
  handleDragStart = (data) => (event) => {
    let fromActionStep = JSON.stringify({ step: data.step, actionId: data.actionId });
    event.dataTransfer.setData('dragContent', fromActionStep);
  };

  /** The dragover event is fired when an element or text selection is being dragged
   * over a valid drop target (every few hundred milliseconds)
   * The event is fired on the drop target(s)
   * */
  handleDragOver = (data) => (event) => {
    event.preventDefault(); // Necessary. Allows us to drop.
    return false;
  };

  /** Fired when an element or text selection is dropped on a valid drop target
   * The event is fired on the drop target(s)
   * */
  handleDrop = (data) => (event) => {
    event.preventDefault();
    let fromActionStep = JSON.parse(event.dataTransfer.getData('dragContent'));
    let toActionStep = { step: data.step, actionId: data.actionId };
    const isFromAndToSame = _.isEqual(fromActionStep, toActionStep);

    if (!isFromAndToSame) {
      this.swapActionSteps(fromActionStep, toActionStep);
    }
    return false;
  };

  isRedirectAction = (eventActions, namesToCheck) => {
    return (
      eventActions instanceof Array &&
      eventActions &&
      eventActions.some((e) => namesToCheck.some((name) => e.name.includes(name)))
    );
  };

  render() {
    const {
      eventActions,
      events,
      actions,
      projectId,
      version,
      selectedAction,
      showLoaderForAction,
      showLoaderForDeleteAction,
      showLoaderForSwapAction,
      showLoaderForEnableAction,
    } = this.props;
    const { selectedEvent, isOpenActionPropModal, isOpenActionEnableModal } = events;

    return (
      <div className="event-editor">
        <div className="row">
          <div className="mx-auto col-md-6 text-center my-2 px-0">
            <div
              className="card br-50 mt-0 event-action event-action-start mx-auto"
              style={{ width: '10vw' }}
            >
              <div className="card-body">
                <h6 className="avatar__title js-user-name pb-1 pt-1 my-auto">Start</h6>
              </div>
            </div>

            <svg viewBox="0 0 10 100">
              <line x1="5" x2="5" y1="0" y2="100" />
            </svg>
            {eventActions instanceof Array &&
              eventActions
                .filter((action) => !!action.step)
                .sort((a, b) => {
                  return a.step > b.step ? 1 : a.step == b.step ? 0 : -1;
                })
                .map((action, key) => (
                  <Fragment key={key}>
                    <NewActionDetail
                      projectId={projectId}
                      version={version}
                      selectedEvent={selectedEvent}
                      selectedAction={selectedAction}
                      actions={actions}
                      events={events}
                      hideModal={this.hideFormModal}
                      key={key}
                      action={action}
                      showLoaderForAction={showLoaderForAction}
                      showLoaderForDeleteAction={showLoaderForDeleteAction}
                      changeSelectedIndex={this.changeSelectedIndex}
                      draggable={disallowedActionNames.includes(action.name) ? false : true}
                      onDragStart={this.handleDragStart}
                      onDragOver={this.handleDragOver}
                      onDrop={this.handleDrop}
                      showLoaderForSwapAction={showLoaderForSwapAction}
                    />
                    <svg viewBox="0 0 10 100">
                      <line x1="5" x2="5" y1="0" y2="100" />
                    </svg>
                  </Fragment>
                ))}
            <div className="mx-auto" style={dropdownStyle}>
              <div className="dropdown">
                <button
                  id="dropdown-custom-components"
                  role="button"
                  data-toggle="dropdown"
                  className={`btn btn-fab btn-round ${
                    !this.isRedirectAction(eventActions, disallowedActionNames)
                      ? 'btn-theme-submit'
                      : 'cb disabled'
                  }`}
                  onClick={() => this.changeSelectedIndex(0)}
                  title={
                    !this.isRedirectAction(eventActions, disallowedActionNames)
                      ? 'Add Action'
                      : 'Not allowed to add action'
                  }
                  disabled={this.isRedirectAction(eventActions, disallowedActionNames)}
                >
                  <i className="wb webconnect-plus-circle-dotted"></i>
                </button>
                {this.showActionCategories()}
              </div>
            </div>
            <div
              className="card br-50 mb-0 event-action event-action-end mx-auto"
              style={{ width: '10vw' }}
            >
              <div className="card-body">
                <h6 className="avatar__title js-user-name pb-1 pt-1 my-auto">End</h6>
              </div>
            </div>
          </div>
          {isOpenActionPropModal ? (
            <ActionFormModal
              show={isOpenActionPropModal}
              hideModal={this.hideFormModal}
              eventAction={selectedAction}
              actions={actions}
              selectedEvent={selectedEvent}
              projectId={projectId}
              version={version}
              showLoaderForAction={showLoaderForAction}
              events={events}
            />
          ) : (
            ''
          )}
          {isOpenActionEnableModal ? (
            <ActionEnableModal
              show={isOpenActionEnableModal}
              hideModal={this.hideEnableFormModal}
              eventAction={selectedAction}
              actions={actions}
              selectedEvent={selectedEvent}
              projectId={projectId}
              version={version}
              showLoaderForAction={showLoaderForAction}
              events={events}
              showLoaderForEnableAction={showLoaderForEnableAction}
            />
          ) : (
            ''
          )}
        </div>
      </div>
    );
  }
}

export default NewEventAction;
